import { History } from "lucide-react";

const BG      = "#0A0A0F";
const SURFACE = "#111116";
const BORDER  = "var(--bc-border, rgba(255,255,255,0.07))";
const MUTED   = "#52525B";
const TEXT    = "#E4E4E7";
const ACCENT  = "#FACC15";   // yellow — distinto da green/violet/orange degli altri panel
const HOME_C  = "#06B6D4";   // cyan
const AWAY_C  = "#F97316";   // orange
const WIN_C   = "#22C55E";
const LOSE_C  = "#EF444466";

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

export default function H2HPanel({ matches, homeTeam, awayTeam }) {
  if (!matches || matches.length === 0) {
    return (
      <div
        data-testid="h2h-panel"
        style={{ background: BG, border: `1px solid ${BORDER}`, borderTop: `2px solid ${ACCENT}` }}
      >
        <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: `1px solid ${BORDER}` }}>
          <History size={14} style={{ color: ACCENT }} />
          <span className="font-heading font-black text-xs uppercase tracking-[0.2em]" style={{ color: ACCENT }}>
            HEAD TO HEAD
          </span>
        </div>
        <div className="px-4 py-8 text-center">
          <span className="font-data text-xs" style={{ color: MUTED }}>Nessun precedente disponibile</span>
        </div>
      </div>
    );
  }

  let homeWins = 0, draws = 0, awayWins = 0;
  matches.forEach((m) => {
    if (m.winner_id === homeTeam.id) homeWins++;
    else if (m.winner_id === awayTeam.id) awayWins++;
    else draws++;
  });

  return (
    <div
      data-testid="h2h-panel"
      style={{ background: BG, border: `1px solid ${BORDER}`, borderTop: `2px solid ${ACCENT}`, overflow: "hidden" }}
    >
      {/* Header */}
      <div
        className="px-4 py-3 flex items-center justify-between"
        style={{
          borderBottom: `1px solid ${BORDER}`,
          background: `linear-gradient(90deg, ${ACCENT}0D 0%, transparent 60%)`,
        }}
      >
        <div className="flex items-center gap-2">
          <History size={14} style={{ color: ACCENT }} />
          <span className="font-heading font-black text-xs uppercase tracking-[0.2em]" style={{ color: ACCENT }}>
            HEAD TO HEAD
          </span>
        </div>
        <span className="font-data text-[10px] uppercase tracking-wide" style={{ color: MUTED }}>
          Ultimi {matches.length} incontri
        </span>
      </div>

      {/* Win/Draw/Loss summary */}
      <div className="grid grid-cols-3" style={{ borderBottom: `1px solid ${BORDER}` }}>
        {/* Home wins */}
        <div
          className="flex flex-col items-center justify-center py-4 gap-1"
          style={{ borderRight: `1px solid ${BORDER}` }}
        >
          <span
            className="font-heading font-black text-3xl tabular-nums"
            style={{ color: WIN_C, textShadow: `0 0 16px ${WIN_C}60` }}
          >
            {homeWins}
          </span>
          <span
            className="font-heading font-bold text-[10px] uppercase tracking-wide truncate max-w-[100px] text-center px-1"
            style={{ color: HOME_C }}
          >
            {homeTeam.name}
          </span>
        </div>

        {/* Draws */}
        <div className="flex flex-col items-center justify-center py-4 gap-1">
          <span className="font-heading font-black text-3xl tabular-nums" style={{ color: MUTED }}>
            {draws}
          </span>
          <span className="font-data text-[10px] uppercase tracking-widest" style={{ color: MUTED }}>
            Pareggi
          </span>
        </div>

        {/* Away wins */}
        <div
          className="flex flex-col items-center justify-center py-4 gap-1"
          style={{ borderLeft: `1px solid ${BORDER}` }}
        >
          <span
            className="font-heading font-black text-3xl tabular-nums"
            style={{ color: "#EF4444", textShadow: "0 0 16px #EF444460" }}
          >
            {awayWins}
          </span>
          <span
            className="font-heading font-bold text-[10px] uppercase tracking-wide truncate max-w-[100px] text-center px-1"
            style={{ color: AWAY_C }}
          >
            {awayTeam.name}
          </span>
        </div>
      </div>

      {/* Column headers */}
      <div
        className="grid items-center px-3 py-1.5"
        style={{
          gridTemplateColumns: "52px 1fr 64px 1fr",
          borderBottom: `1px solid ${BORDER}`,
          background: SURFACE,
        }}
      >
        <span className="font-data text-[10px] uppercase tracking-wider" style={{ color: MUTED }}>Data</span>
        <span className="font-data text-[10px] uppercase tracking-wider text-right pr-2" style={{ color: MUTED }}>Casa</span>
        <span className="font-data text-[10px] uppercase tracking-wider text-center" style={{ color: MUTED }}>Ris.</span>
        <span className="font-data text-[10px] uppercase tracking-wider text-left pl-2" style={{ color: MUTED }}>Ospite</span>
      </div>

      {/* Match rows */}
      {matches.map((m, i) => {
        const homeIsRef   = m.home_team === homeTeam.name || (homeTeam.name && m.home_team?.includes(homeTeam.name?.split(" ")[0]));
        const refHomeWon  = m.winner_id === homeTeam.id;
        const refAwayWon  = m.winner_id === awayTeam.id;
        const isDraw      = !m.winner_id;

        // Color for the home/away team name in this row
        const homeNameColor = (() => {
          if (isDraw) return MUTED;
          if (m.winner_id && m.home_team === homeTeam.name) return WIN_C;
          if (m.winner_id && m.home_team === awayTeam.name) return WIN_C;
          return LOSE_C;
        })();
        const awayNameColor = (() => {
          if (isDraw) return MUTED;
          if (m.winner_id && m.away_team === homeTeam.name) return WIN_C;
          if (m.winner_id && m.away_team === awayTeam.name) return WIN_C;
          return LOSE_C;
        })();

        // Score color: green if home won, red if away won, muted if draw
        const homeGoalColor = m.home_goals > m.away_goals ? WIN_C : (m.home_goals < m.away_goals ? "#EF4444" : MUTED);
        const awayGoalColor = m.away_goals > m.home_goals ? WIN_C : (m.away_goals < m.home_goals ? "#EF4444" : MUTED);

        return (
          <div
            key={i}
            data-testid={`h2h-row-${i}`}
            className="grid items-center px-3 py-2.5"
            style={{
              gridTemplateColumns: "52px 1fr 64px 1fr",
              borderBottom: i < matches.length - 1 ? `1px solid ${BORDER}` : "none",
              background: i % 2 === 0 ? "transparent" : `${SURFACE}80`,
            }}
          >
            {/* Date */}
            <span className="font-data text-[11px] tabular-nums" style={{ color: MUTED }}>
              {formatDate(m.date)}
            </span>

            {/* Home team name — right aligned */}
            <span
              className="font-heading font-bold text-xs text-right pr-2 truncate"
              style={{ color: TEXT }}
              title={m.home_team}
            >
              {m.home_team}
            </span>

            {/* Score — centered, fixed width */}
            <div className="flex items-center justify-center gap-1 tabular-nums">
              <span className="font-heading font-black text-sm" style={{ color: homeGoalColor }}>
                {m.home_goals}
              </span>
              <span className="font-data text-xs" style={{ color: MUTED }}>–</span>
              <span className="font-heading font-black text-sm" style={{ color: awayGoalColor }}>
                {m.away_goals}
              </span>
            </div>

            {/* Away team name — left aligned */}
            <span
              className="font-heading font-bold text-xs text-left pl-2 truncate"
              style={{ color: TEXT }}
              title={m.away_team}
            >
              {m.away_team}
            </span>
          </div>
        );
      })}
    </div>
  );
}
